const API_BASE = location.origin

export interface User {
  id: string
  email: string
  name: string
  needsPassword?: boolean
  isAdmin?: boolean
  createdAt?: number
  sentWelcomeEmail?: boolean
  hasAvatar?: boolean
  lastSeenAt?: number
  tracksCount?: number
}

export interface Track {
  id: string
  userId: string
  userName?: string
  userHasAvatar?: boolean
  title: string
  code: string
  isPublic: boolean
  remixOf?: string
  likesCount: number
  commentsCount: number
  createdAt: number
  updatedAt: number
  remixes?: Track[]
  parentTrack?: Track
  isLiked?: boolean
}

export interface PaginatedTracksResponse {
  tracks: Track[]
  cursor?: string
}

export interface Comment {
  id: string
  trackId: string
  userId: string
  userName?: string
  userHasAvatar?: boolean
  text: string
  createdAt: number
}

async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || 'Request failed')
  }

  return response.json()
}

export const api = {
  auth: {
    signUp: (email: string, password: string, name: string) =>
      apiRequest('/api/auth/signup', {
        method: 'POST',
        body: JSON.stringify({ email, password, name }),
      }),

    signIn: (email: string, password: string) =>
      apiRequest('/api/auth/signin', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    signOut: () => apiRequest('/api/auth/signout', { method: 'POST' }),

    getMe: () => apiRequest('/api/auth/me'),

    forgotPassword: (email: string) =>
      apiRequest('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    resetPassword: (token: string, newPassword: string) =>
      apiRequest('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      }),

    setPassword: (password: string) =>
      apiRequest('/api/auth/set-password', {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),

    getGoogleAuthUrl: async () => {
      const response = await apiRequest('/api/auth/google')
      return response.url
    },

    loginAsUser: (userId: string): Promise<{ user: User; sessionId: string }> =>
      apiRequest('/api/auth/login-as-user', {
        method: 'POST',
        body: JSON.stringify({ userId }),
      }),
  },

  users: {
    getAllUsers: (): Promise<{ users: User[] }> => apiRequest('/api/users'),

    getUser: (userId: string): Promise<{ user: User }> => apiRequest(`/api/users/${userId}`),

    getUserTracks: (
      userId: string,
      limit?: number,
      cursor?: string,
    ): Promise<PaginatedTracksResponse> => {
      const params = new URLSearchParams()
      if (limit) params.set('limit', limit.toString())
      if (cursor) params.set('cursor', cursor)
      const query = params.toString() ? `?${params.toString()}` : ''
      return apiRequest(`/api/users/${userId}/tracks${query}`)
    },

    updateUser: (userId: string, data: { name: string }): Promise<{ user: User }> =>
      apiRequest(`/api/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    uploadProfileImage: async (userId: string, imageBlob: Blob): Promise<{ success: boolean }> => {
      const arrayBuffer = await imageBlob.arrayBuffer()
      const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)))
      return apiRequest(`/api/users/${userId}/profile-image`, {
        method: 'PUT',
        body: JSON.stringify({ image: base64 }),
      })
    },

    getProfileImageUrl: (userId: string): string => `${API_BASE}/api/users/${userId}/profile-image`,

    sendWelcomeEmail: (userId: string): Promise<{ success: boolean }> =>
      apiRequest(`/api/users/${userId}/send-welcome-email`, {
        method: 'POST',
      }),

    deleteUser: (userId: string): Promise<{ success: boolean }> =>
      apiRequest(`/api/users/${userId}`, {
        method: 'DELETE',
      }),
  },

  admin: {
    getAllTracks: (limit?: number, cursor?: string): Promise<PaginatedTracksResponse> => {
      const params = new URLSearchParams()
      if (limit) params.set('limit', limit.toString())
      if (cursor) params.set('cursor', cursor)
      const query = params.toString() ? `?${params.toString()}` : ''
      return apiRequest(`/api/admin/tracks${query}`)
    },

    downloadBackup: async (): Promise<Blob> => {
      const response = await fetch(`${API_BASE}/api/admin/backup`, {
        credentials: 'include',
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }))
        throw new Error(error.error || 'Request failed')
      }

      return response.blob()
    },
  },

  tracks: {
    save: (title: string, code: string, isPublic: boolean, remixOf?: string) =>
      apiRequest('/api/tracks', {
        method: 'POST',
        body: JSON.stringify({ title, code, isPublic, remixOf }),
      }),

    update: (trackId: string, data: { title?: string; code?: string; isPublic?: boolean }) =>
      apiRequest(`/api/tracks/${trackId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (trackId: string) => apiRequest(`/api/tracks/${trackId}`, { method: 'DELETE' }),

    getUserTracks: (limit?: number, cursor?: string): Promise<PaginatedTracksResponse> => {
      const params = new URLSearchParams()
      if (limit) params.set('limit', limit.toString())
      if (cursor) params.set('cursor', cursor)
      const query = params.toString() ? `?${params.toString()}` : ''
      return apiRequest(`/api/tracks${query}`)
    },

    getTrack: (trackId: string): Promise<{ track: Track }> => apiRequest(`/api/tracks/${trackId}`),

    getPublicTracks: (limit?: number, cursor?: string): Promise<PaginatedTracksResponse> => {
      const params = new URLSearchParams()
      if (limit) params.set('limit', limit.toString())
      if (cursor) params.set('cursor', cursor)
      const query = params.toString() ? `?${params.toString()}` : ''
      return apiRequest(`/api/tracks/public${query}`)
    },

    getRemixes: (trackId: string): Promise<{ remixes: Track[] }> => apiRequest(`/api/tracks/${trackId}/remixes`),

    like: (trackId: string): Promise<{ likesCount: number }> =>
      apiRequest(`/api/tracks/${trackId}/like`, { method: 'POST' }),

    unlike: (trackId: string): Promise<{ likesCount: number }> =>
      apiRequest(`/api/tracks/${trackId}/like`, { method: 'DELETE' }),

    getLikedTracks: (limit?: number, cursor?: string): Promise<PaginatedTracksResponse> => {
      const params = new URLSearchParams()
      if (limit) params.set('limit', limit.toString())
      if (cursor) params.set('cursor', cursor)
      const query = params.toString() ? `?${params.toString()}` : ''
      return apiRequest(`/api/tracks/liked${query}`)
    },

    getLikeStatus: (trackId: string): Promise<{ liked: boolean }> => apiRequest(`/api/tracks/${trackId}/like-status`),

    downloadAllTracksZip: async (): Promise<Blob> => {
      const response = await fetch(`${API_BASE}/api/tracks/download-zip`, {
        credentials: 'include',
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }))
        throw new Error(error.error || 'Request failed')
      }

      return response.blob()
    },

    getComments: (trackId: string): Promise<{ comments: Comment[] }> => apiRequest(`/api/tracks/${trackId}/comments`),

    postComment: (trackId: string, text: string): Promise<{ comment: Comment }> =>
      apiRequest(`/api/tracks/${trackId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      }),

    deleteComment: (trackId: string, commentId: string): Promise<{ success: boolean }> =>
      apiRequest(`/api/tracks/${trackId}/comments/${commentId}`, { method: 'DELETE' }),
  },
}
